
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Phone, 
  Mail, 
  Building2, 
  Euro,
  Calendar,
  Users
} from 'lucide-react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';

interface Lead {
  id: string;
  company_name: string | null;
  email: string | null;
  business_type: string;
  monthly_energy_costs: number;
  energy_potential_score: number | null;
  employee_count: string;
  created_at: string;
  lead_status: string;
  notes: string | null;
  next_action_date: string | null;
  assigned_to: string | null;
}

interface LeadCardProps {
  lead: Lead;
  onDragStart: (e: React.DragEvent) => void;
  onClick: () => void;
}

const LeadCard: React.FC<LeadCardProps> = ({ lead, onDragStart, onClick }) => {
  const getPriorityColor = (score: number | null) => {
    if (!score) return 'bg-gray-100';
    if (score >= 80) return 'bg-green-100 border-green-300';
    if (score >= 60) return 'bg-yellow-100 border-yellow-300';
    return 'bg-red-100 border-red-300';
  };

  const isOverdue = lead.next_action_date && new Date(lead.next_action_date) < new Date();

  return (
    <Card
      className={`cursor-pointer hover:shadow-md transition-all duration-200 border-l-4 ${
        lead.energy_potential_score 
          ? lead.energy_potential_score >= 70 
            ? 'border-l-green-500' 
            : 'border-l-yellow-500'
          : 'border-l-gray-300'
      } ${isOverdue ? 'ring-2 ring-red-200' : ''}`}
      draggable
      onDragStart={onDragStart}
      onClick={onClick}
    >
      <CardContent className="p-4 space-y-3">
        {/* Company Info */}
        <div>
          <h4 className="font-semibold text-sm text-gray-900 truncate">
            {lead.company_name || 'Geen bedrijfsnaam'}
          </h4>
          <p className="text-xs text-gray-500">{lead.business_type}</p>
        </div>

        {/* Key Metrics */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              <Euro className="h-3 w-3 text-gray-400" />
              <span className="text-xs text-gray-600">
                €{lead.monthly_energy_costs.toLocaleString()}/mnd
              </span>
            </div>
            {lead.energy_potential_score && (
              <Badge variant="outline" className="text-xs">
                {lead.energy_potential_score}%
              </Badge>
            )}
          </div>
          
          <div className="flex items-center gap-1">
            <Users className="h-3 w-3 text-gray-400" />
            <span className="text-xs text-gray-600">{lead.employee_count} werknemers</span>
          </div>
        </div>

        {/* Contact Actions */}
        <div className="flex items-center gap-2">
          {lead.email && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={(e) => {
                e.stopPropagation();
                window.open(`mailto:${lead.email}`, '_blank');
              }}
            >
              <Mail className="h-3 w-3" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={(e) => {
              e.stopPropagation();
              // Placeholder for phone action
            }}
          >
            <Phone className="h-3 w-3" />
          </Button>
        </div>

        {/* Next Action */}
        {lead.next_action_date && (
          <div className={`text-xs p-2 rounded ${isOverdue ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'}`}>
            <div className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              <span>
                {format(new Date(lead.next_action_date), 'dd MMM', { locale: nl })}
              </span>
            </div>
          </div>
        )}

        {/* Assigned To */}
        {lead.assigned_to && (
          <div className="text-xs text-gray-500">
            Toegewezen aan: {lead.assigned_to}
          </div>
        )}

        {/* Creation Date */}
        <div className="text-xs text-gray-400 border-t pt-2">
          {format(new Date(lead.created_at), 'dd MMM yyyy', { locale: nl })}
        </div>
      </CardContent>
    </Card>
  );
};

export default LeadCard;
